import { useEffect, useRef } from 'react'
import { getThread } from '@/lib/api/threads'
import { isOfflineOrUnreachableFetchError } from '@/lib/api/core'
import {
  canRestoreAgentPanelThread,
  shouldReleaseAgentPanelForWorkspaceFilter,
  shouldUnbindAgentPanelForWorkspacePass,
} from '@/lib/chat-conversation-scope'
import {
  peekThreadWorkspaceMeta,
  rememberThreadWorkspaceMeta,
} from '@/lib/thread-workspace-meta'
import { useWorkspace } from '@/providers/workspace-provider'
import { useChatSurfaceRegistry } from './use-chat-surface-registry'

/**
 * Unbind the agent panel when the hosted thread's tags do not overlap the
 * sidebar active workspaces (e.g. Personal chat still open after navigating
 * to Kootenay Lightweb). Last-thread id stays in sessionStorage so returning
 * to an overlapping workspace can restore.
 */
export function useAgentPanelWorkspaceGuard(): void {
  const registry = useChatSurfaceRegistry()
  const { activeWorkspaceIds } = useWorkspace()
  const threadId = registry.agentPanelThreadId
  const release = registry.releaseAgentPanelToWelcome
  const restore = registry.restoreLastAgentPanelThread
  const peekStored = registry.peekLastAgentPanelThreadId
  const activeKey = activeWorkspaceIds.join('|')
  const activeIdsRef = useRef(activeWorkspaceIds)
  const releaseRef = useRef(release)
  const restoreRef = useRef(restore)
  const peekStoredRef = useRef(peekStored)
  activeIdsRef.current = activeWorkspaceIds
  releaseRef.current = release
  restoreRef.current = restore
  peekStoredRef.current = peekStored
  const inFlightRef = useRef<string | null>(null)
  const prevActiveKeyRef = useRef<string | null>(null)

  useEffect(() => {
    const ids = activeIdsRef.current
    const filterChanged = prevActiveKeyRef.current !== null && prevActiveKeyRef.current !== activeKey
    prevActiveKeyRef.current = activeKey

    if (threadId?.startsWith('new-')) return

    if (!threadId) {
      // Only restore after a workspace-filter change. Do not refill Welcome on
      // every empty-panel render (that would steal a move-to-editor or New chat).
      if (!filterChanged) return
      const stored = peekStoredRef.current()
      if (stored && canRestoreAgentPanelThread(peekThreadWorkspaceMeta(stored), ids)) {
        restoreRef.current('workspace-overlap', { activeWorkspaceIds: ids })
      }
      return
    }

    if (
      !shouldUnbindAgentPanelForWorkspacePass({
        filterChanged,
        threadId,
        activeWorkspaceCount: ids.length,
      })
    ) {
      return
    }

    const cached = peekThreadWorkspaceMeta(threadId)
    // Empty tags are not authoritative (GET /threads/:id used to omit them and
    // wipe the list-API cache). Re-fetch so organizationId / real tags can unbind.
    const tagsKnown = Boolean(cached?.taggedOrganizationIds?.length)
    if (cached && tagsKnown) {
      if (shouldReleaseAgentPanelForWorkspaceFilter(cached, ids)) {
        releaseRef.current('workspace-mismatch')
      }
      return
    }

    const flightKey = `${threadId}:${activeKey}`
    if (inFlightRef.current === flightKey) return
    inFlightRef.current = flightKey

    let cancelled = false
    void getThread(threadId)
      .then((thread) => {
        const meta = rememberThreadWorkspaceMeta(thread)
        if (cancelled) return
        if (shouldReleaseAgentPanelForWorkspaceFilter(meta, activeIdsRef.current)) {
          releaseRef.current('workspace-mismatch')
        }
      })
      .catch((err) => {
        if (isOfflineOrUnreachableFetchError(err)) return
        const status = (err as { status?: number })?.status
        if (status === 404 || status === 403) return
        console.warn('[agent-panel-workspace-guard] thread lookup failed:', err)
      })
      .finally(() => {
        if (inFlightRef.current === flightKey) inFlightRef.current = null
      })

    return () => {
      cancelled = true
    }
  }, [threadId, activeKey])
}
